import BigNumber from 'bignumber.js'
import Toast from 'components/Toast'
import { wsUrl } from 'config'
import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import useWebSocket from 'react-use-websocket'
import { useAppDispatch } from 'state/hooks'
import { useUser } from 'state/user/hooks'
import { updateProcessing, updateRefreshNftType } from 'state/user/reducer'
import dayjs from 'dayjs'
import Logo from 'assets/images/svg/common/logo.svg'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

export default function WebSocket() {
  const [socketUrl, setSocketUrl] = useState<string | null>(null)
  const [timeout] = useState(20000)
  const [documentHidden, setDocumentHidden] = useState(true)
  const [reconnectTime] = useState(20000)
  const [reconnectTimer, setReconnectTimer] = useState<NodeJS.Timeout | null>(null)
  const [heartTimeOut, setHeartTimeOut] = useState<NodeJS.Timeout | null>(null)
  const [serverHeartTimeOut, setServerHeartTimeOut] = useState<NodeJS.Timeout | null>(null)
  const [reconnectCount, setReconnectCount] = useState<number>(10)
  const dispatch = useAppDispatch()
  const user = useUser()
  useEffect(() => {
    window.addEventListener('visibilitychange', () => {
      setDocumentHidden(document.hidden)
    })

    return () => {
      window.removeEventListener('visibilitychange', () => {
        setDocumentHidden(document.hidden)
      })
    }
  })
  const { lastMessage, getWebSocket, sendMessage } = useWebSocket(socketUrl, {
    onOpen: () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
      }
      sendMessage(
        JSON.stringify({
          uid: user?.uid || '',
          token: localStorage.getItem('token'),
        })
      )
      setReconnectCount(10)
      // 建立心跳机制
      reset()
      start()
    },
    onClose: () => {
      reset()
      onReconnect()
    },
  })

  const reset = useCallback(() => {
    if (heartTimeOut) {
      clearTimeout(heartTimeOut)
    }
    if (serverHeartTimeOut) {
      clearTimeout(serverHeartTimeOut)
    }
  }, [heartTimeOut, serverHeartTimeOut])

  const destroy = useCallback(() => {
    reset()
    getWebSocket()?.close()
    setReconnectTimer(null)
  }, [getWebSocket, reset])

  useEffect(() => {
    if (user) {
      setSocketUrl(wsUrl)
    } else {
      destroy()
      setSocketUrl(null)
    }
  }, [destroy, user, user?.uid])

  const messageHandle = useCallback(
    (lastMessage: MessageEvent) => {
      const { code, data } = JSON.parse(lastMessage.data)
      if (code === 200 && data.indexOf('pong!') === -1) {
        const result = JSON.parse(data)
        if (result.event === 'process') {
          dispatch(updateProcessing(result.data))
        } else if (result.event === 'notice') {
          const noticeData = JSON.parse(result.data)
          if (result.status === 2) {
            const _amount = `${new BigNumber(noticeData.amount).decimalPlaces(6, 1).toString()} ${noticeData.symbol}`
            toast.success(
              <Toast
                title={`${
                  noticeData.type === 1 ? 'Withdrawal' : noticeData.type === 2 ? 'Swap' : 'Deposit'
                } Successful`}
                linkText="Check Records"
                message={
                  noticeData.type !== 2
                    ? `${_amount} has been ${noticeData.type === 1 ? 'withdrawn from' : 'deposit to'} your wallet.`
                    : `${_amount} swapped and added to your ${noticeData.toChain} balance`
                }
                link="/records"
              ></Toast>
            )
          } else {
            const _amount = `${new BigNumber(noticeData.amount).decimalPlaces(6, 1).toString()} ${noticeData.symbol}`
            toast.error(
              <Toast
                title={`${noticeData.type === 1 ? 'Withdrawal' : noticeData.type === 2 ? 'Swap' : 'Deposit'} Fail`}
                linkText="Check Records"
                message={
                  noticeData.type === 1
                    ? `Failed to withdraw ${_amount} from your wallet.`
                    : noticeData.type === 2
                    ? `Failed to swap ${_amount} from ${noticeData.fromChain} to ${noticeData.toChain}.`
                    : `${_amount} has been deposit to your wallet.`
                }
                link="/records"
              ></Toast>
            )
          }
        } else if (result.event === 'uni_swap') {
          const noticeData = JSON.parse(result.data)
          const fromCoin = noticeData.fromCoin === 'PKTK' ? '$PKTK' : noticeData.fromCoin
          const toCoin = noticeData.toCoin === 'PKTK' ? '$PKTK' : noticeData.toCoin

          if (result.status === 2) {
            const _amount = `${new BigNumber(noticeData.amount).decimalPlaces(2, 1).toString()}`
            toast.success(
              <Toast
                title={`Swap Successful！`}
                linkText="Check History"
                message={`Swapped ${_amount} ${fromCoin} into ${toCoin}.`}
                link="/records"
              ></Toast>
            )
          } else {
            const _amount = `${new BigNumber(noticeData.amount).decimalPlaces(2, 1).toString()}`
            toast.error(
              <Toast
                title={`Swap Failed!`}
                linkText="Check History"
                message={`Fail to swap ${_amount} ${fromCoin} into ${toCoin}.`}
                link="/records"
              ></Toast>
            )
          }
        } else if (result.event === 'nft') {
          const noticeData = JSON.parse(result.data)
          const biz = noticeData.bizType
          let title = ''
          let message = ''
          dispatch(updateRefreshNftType(biz))
          if (result.status === 1) {
            if (biz === '03') {
              title = 'Crafting Successful'
              message = `You have successfully crafted ${noticeData.nftName}`
            } else if (biz === '06') {
              title = 'Purchased Successful'
              message = `You have successfully purchased the NFT ${noticeData.nftName}`
            } else if (biz === '09') {
              title = 'Minting Successful'
              message = `You have successfully minted ${noticeData.nftName}`
            } else if (biz === '19') {
              title = 'Deposited'
              message = `Congratulations, you have recharged your NFT successfully, please check in my NFT`
            } else if (biz === '17') {
            }
            title &&
              toast.success(
                <Toast title={title} linkText="View details" message={message} link="/mynft?type=sell"></Toast>
              )
          } else if (result.status === 0) {
            if (biz === '03') {
              title = 'Crafting Failed'
              message = `Failed to craft ${noticeData.nftName}`
            } else if (biz === '06') {
              title = 'Purchase Failed'
              message = `Failed to purchase ${noticeData.nftName}`
            } else if (biz === '09') {
              title = 'Minting Failed'
              message = `Failed to mint NFT ${noticeData.nftName}`
            } else if (biz === '19') {
              title = 'Deposited'
              message = `Congratulations, you have recharged your NFT successfully, please check in my NFT`
            } else if (biz === '17') {
            }
            title &&
              toast.error(
                <Toast title={title} linkText="View details" message={message} link="/mynft?type=sell"></Toast>
              )
          }
        } else if (result.event === 'sub_activity') {
          const subActivityData = JSON.parse(result.data)
          // const gameName = subActivityData.gameName.replace(/\s+/g, '').toLocaleLowerCase()
          if (subActivityData.type === '1') {
            toast.success(
              <Toast
                linkText="Join now"
                message={`${subActivityData.gameName} ${
                  subActivityData.activityName
                } is coming soon!It will start at ${dayjs
                  .utc(new Date(subActivityData.startTime))
                  .format('YYYY-MM-DD HH:mm:ss')}.Click the button below to get joined!`}
                // link={subActivityData.url ? subActivityData.url : undefined`/mint/${gameName}?gameMintActivityId=${subActivityData.activityMintId}`}
                link={subActivityData.url ? subActivityData.url : undefined}
              ></Toast>
            )
          } else if (subActivityData.type === '2') {
            toast.success(
              <Toast
                linkText="Join now"
                message={`${subActivityData.gameName} ${subActivityData.activityName} is under way!Welcome to join!.Click the button below to get joined!`}
                link={subActivityData.url ? subActivityData.url : undefined}
              ></Toast>
            )
          }
        } else if (result.event === 'desktopNotifications') {
          const notificationData = JSON.parse(result.data)
          if (documentHidden) {
            toast.success(
              <Toast
                linkText="View details"
                title={notificationData.title}
                message={notificationData.content}
                link={notificationData.url}
              ></Toast>
            )
          } else if (window.Notification.permission === 'granted') {
            const notify = new window.Notification(notificationData.title, {
              body: notificationData.content,
              data: {
                id: notificationData.id,
                url: notificationData.url,
              },
              icon: Logo,
            })
            notify.onclick = (e: any) => {
              if (e.target) {
                // console.log(e.target.data.id)
                window.location.href = e.target.data.url
              }
            }
          }
        }
      }
    },
    [dispatch, documentHidden]
  )

  useEffect(() => {
    if (lastMessage !== null) {
      messageHandle(lastMessage)
    }
  }, [lastMessage, messageHandle])

  /**
   * 启动心跳
   */
  const start = () => {
    setHeartTimeOut(
      setTimeout(() => {
        sendMessage('ping')
        setServerHeartTimeOut(
          setTimeout(() => {
            sendMessage('ping')
            // 重新开始检测
            reset()
            start()
          }, timeout)
        )
      }, timeout)
    )
  }

  /**
   * 连接事件
   */
  const onReconnect = () => {
    if (reconnectCount > 0 || reconnectCount === -1) {
      setReconnectTimer(
        setTimeout(() => {
          setSocketUrl(wsUrl)
          if (reconnectCount !== -1) {
            setReconnectCount(reconnectCount - 1)
          }
        }, reconnectTime)
      )
    } else {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
      }
      setReconnectCount(10)
    }
  }

  return <></>
}
